import { Text, View } from '@react-pdf/renderer';
import logo from '@ui/assets/images/USA/USA_FASTENER_RGB_PRIMARY_LOGO_AW.png';
import { EnvConfig } from '@ui/config/env';

import { usePDFContext } from '../PDFProvider';
import { EmployeeInformation } from './components/EmployeeInformation';
import { Header } from './components/Header';
import { Logo } from './components/Logo';
import { Page } from './components/Page';
import { CustomerAddress } from './components/QuoteDetails/CustomerAddress';
import { CustomerContact } from './components/QuoteDetails/CustomerContact';
import { QuoteDetails } from './components/QuoteDetails/QuoteDetails';
import { QuoteInformation } from './components/QuoteDetails/QuoteInformation';
import { QuoteId } from './components/QuoteId';
import { QuoteLineForPart, QuoteLineForServices } from './components/QuoteLine';
import { EstimatedQuantityOnHand } from './components/QuoteLine/EstimatedQuantityOnHand';
import { PartDescription } from './components/QuoteLine/PartDescription';
import { PartId } from './components/QuoteLine/PartId';
import { PartSpecs } from './components/QuoteLine/PartSpecs';
import { QuoteTotal } from './components/QuoteTotal';
import { SiteInformation } from './components/SiteInformation';
import { Specs } from './components/Specs';
import { styles } from './components/styles';

const USA_SITE_INFORMATION = [
  '1300 Gazin St',
  'Houston, TX 77020',
  'Phone (713) 641-4600',
];

export function USAQuoteDocument() {
  const { quoteLines } = usePDFContext();

  return (
    <Page>
      <Header>
        <Logo
          source={logo}
          text="ISO 9001:2015"
          // Extra styles can be added to fine tune the logo
          extraStyles={[{ height: '42px' }]}
        />

        <SiteInformation name="USA Fastener" items={USA_SITE_INFORMATION} />

        {/* Some sites may have a different ecommerce site */}
        <QuoteId ecommerceUri={EnvConfig.getKey('MW_COMPONENTS_URI')} />
      </Header>

      <QuoteDetails>
        <CustomerAddress />

        <CustomerContact />

        <QuoteInformation showFOB />
      </QuoteDetails>

      {quoteLines.map((line) => {
        const { part, ...lineProps } = line;
        if (!part) {
          return <QuoteLineForServices {...line} showDescription />;
        }
        return (
          <QuoteLineForPart
            key={line.id}
            {...lineProps}
            part={part}
            showDrawing
          >
            <PartId part={part} showCustomerPartId />

            {/* Most site display description */}
            <PartDescription description={part.description} />

            <PartSpecs specs={line.specs} />

            {/* Most sites do not display quantity */}
            <EstimatedQuantityOnHand />
          </QuoteLineForPart>
        );
      })}

      <QuoteTotal />

      <Specs />

      <EmployeeInformation />

      {/* The rest of this is completely custom per site */}

      <View
        style={[
          styles.row,
          {
            borderTopWidth: '1px',
            borderTopColor: '#000',
            borderTopStyle: 'dashed',
            marginTop: 10,
          },
        ]}
      >
        <View style={styles.sectionHalf}>
          <Text style={styles.textSmall}>
            All custom generated items are non-refundable / non-returnable.
          </Text>
        </View>
        <View style={styles.sectionHalf}>
          <Text style={styles.textSmall}>
            Stock item returns are subject to a minimum 20% restocking fee.
          </Text>
        </View>
      </View>

      <View style={styles.row}>
        <View style={{ ...styles.fullRow, ...styles.gap4 }}>
          <Text style={styles.textSmall}>
            MTR’s provided at no cost with purchase order request and will be
            delivered with shipped goods. Requests after shipment for MTR’s -
            $10.00 each
          </Text>
          <Text style={styles.textSmall}>
            Orders less than $50.00 are subject to a $10.00 processing fee.
          </Text>
          <Text style={styles.textSmall}>
            Please contact us if delivery quoted does not meet your
            requirements.
          </Text>
          <Text style={styles.textSmall}>
            Typographical and clerical errors are subject to correction.
          </Text>
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.fullRow}>
          <Text style={styles.textSmall}>
            This quotation is made without benefit of reviewing any PO
            directions involving: dimensional or performance specifications,
            process directions, shipping requirements, QC clauses, or any other
            requirement that may additionally impact the cost of producing the
            quoted product. If such direction occurs with your submission of a
            PO, USA Fasteners reserves the right to increase the price of the
            quoted product to compensate for the added activity.
          </Text>
        </View>
      </View>
    </Page>
  );
}
